import React from 'react';

export interface ReviewSummary {
  source: string;
  destination: string;
  fieldsMapped?: number;
}

export interface NameStepProps {
  name: string;
  onChange: (name: string) => void;
  onActivate: () => void;
  activating: boolean;
  activateError: string | null;
  activateSuccess?: boolean;
  integrationName?: string;
  onDone?: () => void;
  onCreateAnother?: () => void;
  isEditing?: boolean;
  reviewSummary?: ReviewSummary;
}

export const NameStep: React.FC<NameStepProps> = ({
  name,
  onChange,
  onActivate,
  activating,
  activateError,
  activateSuccess = false,
  integrationName,
  onDone,
  onCreateAnother,
  isEditing = false,
  reviewSummary,
}) => {
  if (activateSuccess) {
    return (
      <div
        className="flex w-full min-w-0 flex-col items-center gap-5 overflow-x-hidden py-2 text-center"
        data-testid="activate-success"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-green-600"
            aria-hidden="true"
          >
            <path d="M4 13l6 6 12-12" />
          </svg>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-[14px] font-semibold text-neutral-900">You&apos;re all set.</p>
          <p
            className="text-[12px] leading-relaxed text-neutral-500"
            data-testid="activate-success-message"
          >
            Your leads will now be sent to {integrationName ?? 'your integration'}.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2">
          <button
            onClick={onDone}
            data-testid="activate-done-button"
            className="flex w-full cursor-pointer items-center justify-center rounded-[8px] bg-violet-600 px-4 py-[9px] text-[13px] font-medium text-white transition-colors hover:bg-violet-700"
          >
            View all automations
          </button>
          {onCreateAnother && (
            <button
              onClick={onCreateAnother}
              data-testid="activate-create-another-button"
              className="flex w-full cursor-pointer items-center justify-center rounded-[8px] border border-neutral-200 px-4 py-[9px] text-[13px] font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
            >
              + Create another
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden" data-testid="name-step">
      <div>
        <p className="mb-1 text-[10.5px] font-bold tracking-[0.06em] text-[#9898A2] uppercase">
          Name your automation
        </p>
        <p className="text-xs leading-relaxed text-neutral-500">
          Give it a name so you can easily find it later.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="automation-name" className="text-[11px] font-medium text-neutral-600">
          Automation name <span className="ml-0.5 text-red-500">*</span>
        </label>
        <input
          id="automation-name"
          type="text"
          value={name}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. My Page → Mailchimp"
          maxLength={255}
          autoFocus
          data-testid="automation-name-input"
          className="h-8 w-full rounded-lg bg-neutral-50 px-2 text-[13px] text-neutral-900 ring-1 ring-neutral-200 outline-none focus:ring-indigo-500"
        />
      </div>

      {reviewSummary && (
        <div
          className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5"
          data-testid="review-summary"
        >
          <p className="mb-2 text-[10px] font-bold tracking-[0.06em] text-neutral-400 uppercase">
            Review
          </p>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-neutral-500">Source</span>
              <span
                className="text-[12px] font-medium text-neutral-800"
                data-testid="review-source"
              >
                {reviewSummary.source}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-neutral-500">Destination</span>
              <span
                className="text-[12px] font-medium text-neutral-800"
                data-testid="review-destination"
              >
                {reviewSummary.destination}
              </span>
            </div>
            {reviewSummary.fieldsMapped !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-neutral-500">Fields mapped</span>
                <span
                  className="text-[12px] font-medium text-neutral-800"
                  data-testid="review-fields-mapped"
                >
                  {reviewSummary.fieldsMapped === 1
                    ? '1 field'
                    : `${reviewSummary.fieldsMapped} fields`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {activateError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5" role="alert">
          <p className="text-[11px] leading-relaxed text-red-600">{activateError}</p>
        </div>
      )}

      <button
        onClick={onActivate}
        disabled={activating || !name.trim()}
        data-testid="activate-button"
        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[8px] bg-violet-600 px-4 py-[9px] text-[13px] font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {activating && (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        )}
        {activating ? (isEditing ? 'Updating…' : 'Activating…') : isEditing ? 'Update' : 'Activate'}
      </button>
    </div>
  );
};
